import React, { useEffect, useState } from 'react';
import API from '../api';
import DMChat from './DMChat';
import SupportForm from './SupportForm';

/**
 * "Something's wrong" — the signed-in half.
 *
 * Reached from the ? in the header, which is on every screen at every width.
 * That placement is the point of the whole thing: the DM system could already
 * carry this conversation, but the only way into it was to tap somebody on a
 * leaderboard, and a person who has just signed up has no pool, so no board, so
 * nobody to tap. The one group guaranteed to be locked out of asking for help
 * was the group most likely to need it.
 *
 * The contact is fetched rather than hard-coded, so renaming or handing over the
 * owner account doesn't quietly break this. When there is no contact at all the
 * modal says so plainly instead of opening a thread with nobody, because an
 * unanswered message is worse than an honest dead end.
 */
export default function SupportModal({ me, onClose }) {
  const [contact, setContact] = useState(undefined);   // undefined = still asking

  // Exactly one person opens this and finds themselves on the other end of it,
  // and it's the person who ships the app. The DM route rightly refuses to let
  // anyone message themselves, so without this the owner gets a chat box that
  // 400s on Send — a broken button, in the one place whose whole promise is that
  // somebody is listening.
  const isOwner = !!(contact && me && contact.id === me.id);

  useEffect(() => {
    API.get('/support/contact')
      .then(res => setContact(res.data?.contact || null))
      .catch(() => setContact(null));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(160deg,#111827,#0a0e1a)',
          border: '1px solid rgba(127,179,255,0.2)', borderRadius: 16,
          width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '18px 22px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div>
            <div style={{
              fontSize: 11, color: '#7fb3ff', letterSpacing: 2,
              fontFamily: "'Oswald', sans-serif", fontWeight: 700, textTransform: 'uppercase'
            }}>Need a hand</div>
            <div style={{
              fontSize: 22, fontFamily: "'Oswald', sans-serif", fontWeight: 700,
              color: '#fff', marginTop: 2
            }}>{isOwner ? "You're the one who gets these" : 'Message the guy who built it'}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
              color: '#aab', fontSize: 18, width: 34, height: 34,
              borderRadius: 8, cursor: 'pointer', fontWeight: 700, flexShrink: 0
            }}
            title="Close (Esc)"
          >×</button>
        </div>

        <div style={{ padding: '16px 22px 22px' }}>
          <div style={{ color: '#889', fontSize: 13, lineHeight: 1.6, marginBottom: 14 }}>
            {isOwner
              ? "This is the button everyone else taps to reach you. Their messages arrive in your DMs. Anyone who wasn't signed in when they wrote lands in the support inbox on the Admin page instead."
              : "Something broken, a score that looks wrong, a question about how any of this works: send it here. It goes straight to a real person, not a help desk. You'll usually get an answer the same day, and at the latest the next one."}
          </div>

          {/* Offered above the message box on purpose. A good share of what
              arrives here is not a fault, it is somebody who has not been shown
              how the app works, and they should not have to wait on a reply to
              find that out. */}
          {!isOwner && (
            <a
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
                padding: '13px 16px', borderRadius: 10, textDecoration: 'none',
                background: 'rgba(127,179,255,0.10)', border: '1px solid rgba(127,179,255,0.4)'
              }}
            >
              <span style={{ fontSize: 20 }}>📖</span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', color: '#cfe4ff', fontSize: 14, fontWeight: 700 }}>
                  New to this? Read the how-to first
                </span>
                <span style={{ display: 'block', color: '#889', fontSize: 12.5, marginTop: 1 }}>
                  Making your picks and reading the scoreboard, with pictures
                </span>
              </span>
            </a>
          )}

          {contact === undefined && (
            <div style={{ color: '#667', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              Opening…
            </div>
          )}

          {/* No contact account to open a thread with. Rare, but the answer is
              never a dead end: the same form the login page uses writes straight
              to the support table, which needs no admin and no working mailer. */}
          {contact === null && (
            <SupportForm defaultEmail={me?.email || ''} compact />
          )}

          {contact && !isOwner && (
            <DMChat
              otherUserId={contact.id}
              me={me}
              height={280}
              allowBlock={false}
              title={`💬 SUPPORT: ${String(contact.username).toUpperCase()}`}
              emptyText="Tell us what's going on. The more detail the better."
            />
          )}
        </div>
      </div>
    </div>
  );
}
